import type React from 'react';
import type { ImageBlock, SlateBlock, SlateLeaf } from '@atlas/renderer';
import { createEditor, Editor, BaseEditor, Descendant } from 'slate';
import { withReact, ReactEditor } from 'slate-react';
import { withHistory, HistoryEditor } from 'slate-history';
import withMarkdown from '@atlas/tool/box/slate/withMarkdown';
import withRichTextInsertion from '@atlas/tool/box/slate/withRichTextInsertion';
import withConstants from '@atlas/tool/box/slate/withConstants';
import withImages from '@atlas/tool/box/slate/withImages';
import withLinks from '@atlas/tool/box/slate/withLinks';
import withRestrictions, { TWithRestrictionsValue } from '@atlas/tool/box/slate/withRestrictions';
import withNormalInlineCode from '@atlas/tool/box/slate/withNormalInlineCode';
import withNormalElements from '@atlas/tool/box/slate/withNormalElements';
import withoutFormattingInsideCodeBlock from '@atlas/tool/box/slate/withoutFormattingInsideCodeBlock';

export default function createEditorInstance(options?: TCreateEditorOptions): TEditorInstance {
    return withoutFormattingInsideCodeBlock(
        withNormalElements(
            withNormalInlineCode(
                withLinks(
                    withRestrictions(
                        (() => {
                            const editor = withConstants(
                                withRichTextInsertion(
                                    withMarkdown(withHistory(withReact(Object.assign(createEditor(), { onKeyDown })))),
                                    { supportInlineImages: options?.supportInlineImages },
                                ),
                            );
                            if (!options?.supportInlineImages) return editor;
                            return withImages(editor, options.defaultImagesAttributes, options);
                        })(),
                        options?.restrictions,
                    ),
                ),
            ),
        ),
    );
}

// #region Helpers

const onKeyDown: (event: React.KeyboardEvent) => void = () => {};

// #endregion

// #region Types

export type TEditorInstance = Editor;

export type TCreateEditorOptions = {
    defaultValue?: Descendant[];
    restrictions?: TWithRestrictionsValue[] | null;
    supportInlineImages?: boolean;
    defaultImagesAttributes?: { align: ImageBlock['align'] };
    onError?: (message: string) => void;
    maxImageHeight?: number;
    maxImageWidth?: number;
    setUploading?: (imagesCount: number) => void;
};

declare module 'slate' {
    interface CustomTypes {
        Editor: BaseEditor &
            ReactEditor &
            HistoryEditor & {
                onKeyDown: (event: React.KeyboardEvent) => void;
                maxImageHeight?: number;
                maxImageWidth?: number;
            };
        Element: SlateBlock;
        Text: SlateLeaf;
    }
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('createEditorInstance', async () => {
        it.todo('applies all plugins');
    });
}

// #endregion
